import { useState, useEffect, useCallback } from 'react';

/** Base URL API: DEV = frontend locale ma API su 192.168.1.113; TEST/PROD = stesso host (no REACT_APP_* così TEST non chiama PROD). */
function getApiBaseUrl() {
  if (typeof window === 'undefined' || !window.location) return '';
  const { protocol, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://192.168.1.113/api';
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${protocol}//${hostname}${port}/api`;
}

// Costanti per i tipi di notifica
export const NOTIFICATION_TYPES = {
  ANNOUNCEMENT: 'announcement',
  MESSAGE: 'message',
  COURSE: 'course',
  SYSTEM: 'system',
  ALERT: 'alert'
};

// Costanti per le priorità
export const NOTIFICATION_PRIORITIES = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent'
};

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Funzione per ottenere il token di autenticazione
  const getAuthToken = () => {
    return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  };

  // Carica le notifiche
  const fetchNotifications = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setError('Token di autenticazione non trovato');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/notifications.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Platform': 'web'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch notifications');
      }

      const data = await response.json();
      
      if (data.success) {
        setNotifications(data.notifications || []);
        // Calcola notifiche non lette
        const unread = data.notifications?.filter(n => !n.read_at).length || 0;
        setUnreadCount(unread);
      } else {
        throw new Error(data.error || 'Errore nel caricamento delle notifiche');
      }
    } catch (err) {
      setError(err.message);
      console.error('Errore nel caricamento notifiche:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Segna notifica come letta
  const markAsRead = useCallback(async (notificationId) => {
    const token = getAuthToken();
    if (!token) return false;

    try {
      const url = `${getApiBaseUrl()}/notifications.php?action=read&id=${notificationId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Platform': 'web'
        },
        body: JSON.stringify({
          action: 'read',
          id: notificationId
        })
      });

      if (response.ok) {
        // Aggiorna lo stato locale
        setNotifications(prev => 
          prev.map(n => 
            n.id === notificationId 
              ? { ...n, read_at: new Date().toISOString() }
              : n
          )
        );
        // Aggiorna contatore
        setUnreadCount(prev => Math.max(0, prev - 1));
        return true;
      }
    } catch (err) {
      console.error('Errore nel segnare come letta:', err);
    }
    return false;
  }, []);

  // Segna notifica come non letta
  const markAsUnread = useCallback(async (notificationId) => {
    const token = getAuthToken();
    if (!token) return false;

    try {
      const response = await fetch(`${getApiBaseUrl()}/notifications.php?action=unread`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Platform': 'web'
        },
        body: JSON.stringify({
          action: 'unread',
          id: notificationId
        })
      });

      if (response.ok) {
        // Aggiorna lo stato locale
        setNotifications(prev => 
          prev.map(n => 
            n.id === notificationId 
              ? { ...n, read_at: null }
              : n
          )
        );
        // Aggiorna contatore
        setUnreadCount(prev => prev + 1);
        return true;
      }
    } catch (err) {
      console.error('Errore nel segnare come non letta:', err);
    }
    return false;
  }, []);

  // Cancella notifica
  const deleteNotification = useCallback(async (notificationId) => {
    const token = getAuthToken();
    if (!token) return false;

    try {
      const url = `${getApiBaseUrl()}/notifications.php?id=${notificationId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Platform': 'web'
        },
        body: JSON.stringify({
          id: notificationId
        })
      });

      if (response.ok) {
        // Rimuovi dalla lista locale
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        // Aggiorna contatore se era non letta
        const wasUnread = notifications.find(n => n.id === notificationId && !n.read_at);
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        return true;
      }
    } catch (err) {
      console.error('Errore nella cancellazione:', err);
    }
    return false;
  }, [notifications]);

  // Segna tutte come lette
  const markAllAsRead = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return false;

    try {
      const response = await fetch(`${getApiBaseUrl()}/notifications.php?action=mark_all_read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Platform': 'web'
        },
        body: JSON.stringify({
          action: 'mark_all_read'
        })
      });

      if (response.ok) {
        // Aggiorna tutte le notifiche come lette
        setNotifications(prev => 
          prev.map(n => ({ ...n, read_at: new Date().toISOString() }))
        );
        setUnreadCount(0);
        return true;
      }
    } catch (err) {
      console.error('Errore nel segnare tutte come lette:', err);
    }
    return false;
  }, []);

  // Invia notifica
  const sendNotification = useCallback(async (notificationData) => {
    const token = getAuthToken();
    if (!token) {
      setError('Token di autenticazione non trovato');
      return false;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/notifications.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Platform': 'web'
        },
        body: JSON.stringify(notificationData)
      });

      if (!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Ricarica le notifiche dopo l'invio
        await fetchNotifications();
        return true;
      } else {
        throw new Error(data.error || 'Errore nell\'invio della notifica');
      }
    } catch (err) {
      setError(err.message);
      console.error('Errore nell\'invio notifica:', err);
      return false;
    }
  }, [fetchNotifications]);

  // Carica notifiche al mount
  useEffect(() => {
    fetchNotifications();
    
    // Auto-refresh ogni 30 secondi
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  return {
    notifications,
    loading,
    error,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAsUnread,
    deleteNotification,
    removeNotification: deleteNotification, // Alias per compatibilità
    markAllAsRead,
    sendNotification
  };
};